import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { submitContact } from "@/lib/api";
import { TIDS } from "@/constants/testIds";

const BUDGETS = [
  "< ₹5L / yr",
  "₹5L – ₹15L / yr",
  "₹15L – ₹50L / yr",
  "₹50L+ / yr",
  "Not sure yet",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    brand: "",
    budget: BUDGETS[1],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill name, email and message");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Message received. We'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        brand: "",
        budget: BUDGETS[1],
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <section className="pt-36 pb-16 grain relative overflow-hidden">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10 relative z-10">
          <p className="text-xs uppercase tracking-[0.35em] text-[#1D2AE8]/80 font-semibold">
            Contact · A conversation, not a form
          </p>
          <h1
            className="font-display leading-[0.9] mt-6"
            style={{ fontSize: "clamp(3rem, 10vw, 8rem)" }}
          >
            Say <em>hello</em>.
            <br />
            Let's build something
            <br />
            worth watching.
          </h1>
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Info panel */}
          <div className="lg:col-span-4 space-y-4">
            <div className="glass-light rounded-3xl p-7">
              <p className="text-xs uppercase tracking-[0.25em] text-[#1D2AE8]/70">
                Studio HQ
              </p>
              <p className="font-display text-2xl mt-3 leading-tight">
                Camac Studios, Kolkata
              </p>
              <p className="mt-2 text-sm text-[#1D2AE8]/75 leading-relaxed flex items-start gap-2">
                <MapPin size={14} className="mt-1 shrink-0" /> 4th Floor,
                Camac Street, Kolkata 700016, India
              </p>
            </div>
            <div className="glass-light rounded-3xl p-7">
              <p className="text-xs uppercase tracking-[0.25em] text-[#1D2AE8]/70">
                Direct Line
              </p>
              <p className="mt-3 flex items-center gap-2 text-[#0B0B0B] font-semibold">
                <Mail size={16} /> admin@illustrativedigital.com
              </p>
              <p className="mt-2 flex items-center gap-2 text-[#0B0B0B] font-semibold">
                <Phone size={16} /> +91 33 4001 2020
              </p>
            </div>
            <div className="glass-dark rounded-3xl p-7">
              <p className="text-xs uppercase tracking-[0.25em] text-[#FFC629]">
                Studio Hours
              </p>
              <p className="mt-3 text-white/90 font-display text-xl">
                Mon – Fri · 10:00 – 19:00 IST
              </p>
              <p className="text-sm text-white/70 mt-2">
                We reply to every serious enquiry within 24 hours.
              </p>
            </div>
          </div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            onSubmit={onSubmit}
            className="lg:col-span-8 glass-dark rounded-[32px] p-8 sm:p-10"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#FFC629] font-semibold">
              Project Brief
            </p>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 text-white leading-[0.95]">
              Tell us what you're building.
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/70">
                  Name
                </label>
                <input
                  data-testid={TIDS.contact.name}
                  className="input-glass mt-2"
                  placeholder="Full name"
                  value={form.name}
                  onChange={update("name")}
                  required
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/70">
                  Email
                </label>
                <input
                  data-testid={TIDS.contact.email}
                  type="email"
                  className="input-glass mt-2"
                  placeholder="you@brand.com"
                  value={form.email}
                  onChange={update("email")}
                  required
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/70">
                  Brand / Company
                </label>
                <input
                  data-testid={TIDS.contact.brand}
                  className="input-glass mt-2"
                  placeholder="Optional"
                  value={form.brand}
                  onChange={update("brand")}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/70">
                  Budget
                </label>
                <select
                  data-testid={TIDS.contact.budget}
                  className="input-glass mt-2 appearance-none"
                  value={form.budget}
                  onChange={update("budget")}
                >
                  {BUDGETS.map((b) => (
                    <option key={b} value={b} className="text-black">
                      {b}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-white/70">
                  Tell us about your brand
                </label>
                <textarea
                  data-testid={TIDS.contact.message}
                  rows={6}
                  className="input-glass mt-2 resize-none"
                  placeholder="What are you building? What does success look like in twelve months?"
                  value={form.message}
                  onChange={update("message")}
                  required
                />
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <p className="text-xs text-white/60 max-w-sm">
                We take on a handful of brands each quarter. We reply within 24
                hours, always in person.
              </p>
              <button
                data-testid={TIDS.contact.submit}
                type="submit"
                disabled={loading}
                className="bg-[#FFC629] text-[#0B0B0B] font-semibold rounded-full px-7 py-3.5 inline-flex items-center gap-2 hover:bg-[#FFE0EC] disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {loading ? "Sending…" : "Send brief"}{" "}
                <ArrowUpRight size={18} />
              </button>
            </div>

            {done && (
              <p
                data-testid={TIDS.contact.success}
                className="mt-6 text-sm text-[#FFC629]"
              >
                Brief received — check your inbox for confirmation.
              </p>
            )}
          </motion.form>
        </div>
      </section>
    </div>
  );
}
